import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Visibility, Edit } from '@mui/icons-material';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import { Lecon, Chapitre } from '../../types';
import LeconForm from './LeconForm';
import MobilePreview from '../common/MobilePreview';
import LeconMobileView from './LeconMobileView';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { logger } from '../../lib/logger';
import { adresseExplorateur } from '../../features/contenu/adresses';

/**
 * Éditeur avancé pour les leçons avec prévisualisation mobile en temps réel
 * Layout split-screen: formulaire à gauche, preview mobile à droite
 */
const LeconAdvancedEditor: React.FC = () => {
  const { environment } = useEnvironment();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lecon, setLecon] = useState<Lecon | null>(null);
  const [chapitreTitre, setChapitreTitre] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  // État pour la prévisualisation en temps réel
  const [previewData, setPreviewData] = useState<Omit<Lecon, 'id'> | null>(null);

  // URL de retour pour l'éditeur LaTeX
  const returnPath = `/lecons/${id}/edit-advanced`;

  useEffect(() => {
    const loadLecon = async () => {
      if (!id) {
        setError('ID de leçon manquant');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [leconData, chapitres] = await Promise.all([
          ChapitresLeconsApiService.getLeconById(environment, id),
          ChapitresLeconsApiService.getChapitres(environment),
        ]);

        setLecon(leconData);
        setPreviewData(leconData);

        // Trouver le titre du chapitre
        const chapitre = chapitres.find((ch: Chapitre) => ch.id === leconData.chapitreId);
        if (chapitre) {
          setChapitreTitre(chapitre.titre);
        }
      } catch (err) {
        logger.error('Erreur lors du chargement de la leçon:', err);
        setError('Impossible de charger la leçon');
      } finally {
        setLoading(false);
      }
    };

    loadLecon();
  }, [id, environment]);

  const handleFormChange = (updatedData: Omit<Lecon, 'id'>) => {
    // Mettre à jour le preview en temps réel
    setPreviewData(updatedData);
  };

  const handleSubmit = async (data: Omit<Lecon, 'id'>) => {
    if (!id) return;

    try {
      setSaving(true);
      await ChapitresLeconsApiService.updateLecon(environment, id, data);
      navigate(adresseExplorateur({ chapitre: lecon?.chapitreId, ouvert: id }));
    } catch (err) {
      logger.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde de la leçon');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(adresseExplorateur({ chapitre: lecon?.chapitreId, ouvert: id }));
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: '#f5f5f5' }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }} color="textSecondary">
          Chargement de la leçon...
        </Typography>
      </Box>
    );
  }

  if (error || !lecon) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Leçon introuvable'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <IconButton onClick={handleCancel} size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              Édition Avancée: {lecon.titre}
            </Typography>

            <Tooltip title={showPreview ? 'Masquer la prévisualisation' : 'Afficher la prévisualisation'}>
              <IconButton onClick={() => setShowPreview(!showPreview)} color="primary">
                {showPreview ? <Visibility /> : <Edit />}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {'Modifiez la leçon et visualisez les changements en temps réel sur le preview mobile'}
          </Typography>
        </Box>

        {saving && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sauvegarde en cours...
          </Alert>
        )}

        {/* Split Layout */}
        <Grid container spacing={3}>
          {/* Formulaire d'édition - Gauche */}
          <Grid item xs={12} lg={showPreview ? 7 : 12}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <LeconFormWrapper
                lecon={lecon}
                returnPath={returnPath}
                saving={saving}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                onChange={handleFormChange}
              />
            </Paper>
          </Grid>

          {/* Preview Mobile - Droite */}
          {showPreview && (
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 16,
                  maxHeight: 'calc(100vh - 32px)',
                  overflowY: 'auto',
                }}
              >
                <MobilePreview title="Aperçu Mobile en Temps Réel">
                  {previewData ? (
                    <LeconMobileView lecon={previewData} chapitreTitre={chapitreTitre} />
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="400px"
                    >
                      <Typography color="textSecondary">Aucune donnée à prévisualiser</Typography>
                    </Box>
                  )}
                </MobilePreview>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

/**
 * Wrapper pour LeconForm qui surveille les changements
 */
interface LeconFormWrapperProps {
  lecon: Lecon;
  returnPath: string;
  saving?: boolean;
  onSubmit: (data: Omit<Lecon, 'id'>) => void;
  onCancel: () => void;
  onChange: (data: Omit<Lecon, 'id'>) => void;
}

const LeconFormWrapper: React.FC<LeconFormWrapperProps> = ({
  lecon,
  returnPath,
  saving,
  onSubmit,
  onCancel,
  onChange,
}) => {
  return (
    <LeconForm
      lecon={lecon}
      returnPath={returnPath}
      saving={saving}
      onSubmit={onSubmit}
      onCancel={onCancel}
      onChange={onChange}
    />
  );
};

export default LeconAdvancedEditor;
